import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ArrowLeft } from 'lucide-react';
import { PRODUCTS } from '../data/mock';
import { useQuote } from '../context/QuoteContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import EnquiryModal from '../components/EnquiryModal';
import SectionHeading from '../components/SectionHeading';

const ProductsIndex = () => {
  const { openQuote } = useQuote();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-28 md:pt-32" data-testid="products-index-page">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-sm text-stone-500 flex items-center gap-2">
            <Link to="/" className="hover:text-pink-brand transition-colors">Home</Link>
            <ArrowRight className="w-3 h-3" />
            <span className="text-stone-800 font-medium">Products</span>
          </div>
        </div>

        {/* Hero */}
        <section className="pt-14 pb-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <SectionHeading
              eyebrow="Our Signature Range"
              title="Everything We"
              accent="Make"
              subtitle="Six hand-crafted, personalised categories that bring warmth, wow and Instagram to every celebration."
            />
          </div>
        </section>

        {/* Grid — same cards, but full width and no anchor scroll */}
        <section className="section-pad">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {PRODUCTS.map((p, i) => (
                <article
                  key={p.slug}
                  className="coco-card group relative overflow-hidden rounded-3xl bg-white border border-pink-100 shadow-soft flex flex-col"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`products-index-card-${p.slug}`}
                >
                  <Link to={`/products/${p.slug}`} className="relative aspect-[4/3] overflow-hidden bg-blush block">
                    <img
                      src={p.hero}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-white/95 text-pink-brand text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">
                      <Sparkles className="w-3 h-3" /> Signature
                    </span>
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-semibold text-xl text-stone-900 leading-tight">{p.name}</h3>
                    <p className="text-sm text-stone-500 mt-1 italic">{p.tagline}</p>
                    <p className="text-sm text-stone-600 mt-3 line-clamp-3 flex-1">{p.short}</p>
                    <div className="mt-5 flex items-center gap-3">
                      <Link
                        to={`/products/${p.slug}`}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-pink-brand hover:gap-2 transition-all"
                        data-testid={`products-index-view-${p.slug}`}
                      >
                        View page <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <span className="w-px h-4 bg-stone-200" />
                      <button
                        type="button"
                        onClick={() => openQuote({ event_type: p.name, source: `products-index-${p.slug}` })}
                        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900 transition-colors"
                        data-testid={`products-index-enquire-${p.slug}`}
                      >
                        Enquire <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-14 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-pink-brand font-semibold hover:gap-2 transition-all"
                data-testid="products-index-back-home"
              >
                <ArrowLeft className="w-4 h-4" /> Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingButtons />
      <EnquiryModal />
    </div>
  );
};

export default ProductsIndex;
